import CoursesFilter from "../../features/CoursesPage/CoursesFilter/CoursesFilter";
import CoursesHero from "../../features/CoursesPage/CoursesHero/CoursesHero";
import CoursesOffers from "../../features/CoursesPage/CoursesOffers/CoursesOffers";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import "./CoursesPage.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { useIsMount } from "../../hooks/useIsMount";
import { useLocation } from "react-router";
import CardInfoSkeleton from "../../components/CardInfoSkeleton/CardInfoSkeleton";
import { errorHandler } from "../../utils/errorHandler";
import { Helmet } from "react-helmet";
import moment from "moment";
import "moment/locale/es";
import GranDBanner from "../../components/GranDBanner/GranDBanner";
import { getCourseStartDate } from "../../utils/courses-dates";

const PAGE_SIZE = 12;
// Valor del filtro "Tipo de curso" que muestra solo packs
const PACKS_FILTER = 'PACKS';

function useQuery() {
   return new URLSearchParams(useLocation().search)
}

const CoursesPage = () => {
   const [specialtyFilter, setSpecialtyFilter] = useState(null);
   const [academicUnitFilter, setAcademicUnitFilter] = useState(null);
   const [monthFilter, setMonthFilter] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState(null)
   const [allCourses, setAllCourses] = useState([]);
   const [packs, setPacks] = useState([]);
   const [activePage, setPage] = useState(1);
   const isMount = useIsMount();
   const location = useLocation();

   const filterCourses = (_courses) => {
      if (!_courses) return
      //APPLY FILTERS
      let filteredCourses = _courses;
      if (specialtyFilter) {
         filteredCourses = filteredCourses.filter((course) => course?.specialties?.toUpperCase() === specialtyFilter);
      }
      if (academicUnitFilter) {
         filteredCourses = filteredCourses.filter((course) => course.type.toUpperCase() === academicUnitFilter);
      }
      if (monthFilter) {
         filteredCourses = filteredCourses.filter((course) => {
            if (!course.startDate) return false;
            // .clone() preserva el modo UTC del moment (moment(course.startDate) lo re-proyecta a local)
            return course.startDate.clone().locale("es").format("MMMM").toUpperCase() === monthFilter;
         });
      }
      return filteredCourses;
   }

   const paginatedCourses = (_filteredCourses, _page, _pageSize) => {
      if (!_filteredCourses) return
      //APPLY PAGINATION
      const startIndex = (_page - 1) * _pageSize;
      const endIndex = startIndex + _pageSize;

      return _filteredCourses.slice(startIndex, endIndex);
   };

   const query = useQuery()
   const searchQuery = query.get('query') || ''

   // Packs: se muestran solo si los filtros activos los pueden incluir
   const filterPacks = (_packs) => {
      if (!_packs?.length) return [];
      if (monthFilter) return [];
      if (academicUnitFilter && academicUnitFilter !== PACKS_FILTER) return [];
      let filtered = _packs;
      if (specialtyFilter) {
         filtered = filtered.filter(pack => (pack.specialties || [])
            .some(specialty => (specialty?.name || '').toUpperCase() === specialtyFilter));
      }
      if (searchQuery) {
         const q = searchQuery.toLowerCase();
         filtered = filtered.filter(pack => (pack.comercialInformation?.name || '').toLowerCase().includes(q));
      }
      return filtered;
   }

   useEffect(() => {
      // Si falla, la página sigue mostrando los cursos sin la sección de packs
      axios.get(`${process.env.REACT_APP_BACKEND_SERVER}/info/packs`)
         .then(res => setPacks(Array.isArray(res.data) ? res.data : []))
         .catch(err => console.log(err));
   }, []);

   useEffect(() => {
      if (isMount) {
         setIsLoading(true);

         // Filtra webinars outsideCampus y matchea contra la búsqueda
         const matchesSearch = (item, q) => {
            if (item.comercialInformation.outsideCampus) return false;
            const name = item.comercialInformation.name.toLowerCase();
            const type = item.type.toLowerCase();
            const specialty = item.specialties.toLowerCase();
            return name.includes(q) || type.includes(q) || specialty.includes(q);
         };

         // Filtra webinars outsideCampus y agrega startDate (moment UTC) vía util
         const mapWithStartDate = (item) => {
            if (item.comercialInformation.outsideCampus) return null;
            return { ...item, startDate: getCourseStartDate(item) };
         };

         const url = process.env.REACT_APP_BACKEND_SERVER + `/info/academicUnits`;
         axios
            .get(url)
            .then((res) => {

               if (searchQuery) {
                  const filteredOpen = res.data.open
                     .filter(item => matchesSearch(item, searchQuery))
                     .map(mapWithStartDate)
                     .filter(Boolean);

                  const filteredClosed = res.data.close
                     .filter(item => matchesSearch(item, searchQuery))
                     .map(mapWithStartDate)
                     .filter(Boolean);

                  return setAllCourses({
                     open: filteredOpen,
                     close: filteredClosed,
                     pre_inscriptions: res.data.pre_inscription
                  })
               }

               // Sort courses by date
               const pre_inscriptions = res.data.pre_inscription

               const openedCourses = res.data.open.map(mapWithStartDate).filter(Boolean);
               openedCourses.sort((a, b) => b.startDate - a.startDate);

               const closedCourses = res.data.close.map(mapWithStartDate).filter(Boolean);
               closedCourses.sort((a, b) => b.startDate - a.startDate);

               // Calculate upcoming courses (comparamos en UTC a nivel día)
               const upcomingCourses = openedCourses
                  .filter(course => course.startDate && course.startDate.isAfter(moment.utc(), 'day'))
                  .sort((a, b) => a.startDate - b.startDate)
                  .slice(0, 3)


               setAllCourses({
                  open: openedCourses,
                  close: closedCourses,
                  upcomingCourses,
                  pre_inscriptions
               });
            })
            .catch((err) => {
               console.log(err)
               const errorMessage = errorHandler(err)
               setError(errorMessage)
            })
            .finally(() => setIsLoading(false));
      }
   }, [isMount, searchQuery]);

   useEffect(() => {
      const specialty = query.get("especialidad");
      const academicUnit = query.get("tipo");
      const month = query.get("mes");


      if (specialty) setSpecialtyFilter(specialty.toUpperCase());
      if (academicUnit) setAcademicUnitFilter(academicUnit.toUpperCase());
      if (month) setMonthFilter(month.toUpperCase());
   }, [location.search, query]);


   return (
      <>
         <Helmet>
            <title>FORVET | Oferta académica</title>
            <meta name="description"
               content="Diseñados para impulsar tu excelencia académica, nuestros programas te brindarán las herramientas necesarias para destacarte en la profesión. Encuentra, con nosotros, tu camino hacia la especialización." />
            <meta property="og:title" content="FORVET | Oferta académica" />
            <meta property="og:description"
               content="Diseñados para impulsar tu excelencia académica, nuestros programas te brindarán las herramientas necesarias para destacarte en la profesión. Encuentra, con nosotros, tu camino hacia la especialización." />
         </Helmet>
         <MainLayout overflow="hidden">
            {/*< UserLocationModal />*/}
            <section style={{ position: "relative" }}
               className="coursesPage__section--noPadding coursesPage-section-position-custom-coursesHero">
               <CoursesHero />
            </section>

            <section className="coursesPage__section coursesPage-section-position-custom-coursesFilter">
               <CoursesFilter
                  withPacks
                  setSpecialtyFilter={setSpecialtyFilter}
                  setAcademicUnitFilter={setAcademicUnitFilter}
                  setMonthFilter={setMonthFilter}
               />
            </section>

            <section className="coursesPage__section">
               {(error && !isLoading) &&
                  <p style={{ fontSize: 'var(--fs-body)', textAlign: 'center' }}>
                     {error}
                  </p>
               }

               {(isLoading && !error) &&
                  <div className="course-offers-container">
                     <CardInfoSkeleton height={200} />
                  </div>
               }

               {(!isLoading && !error && allCourses.open) &&
                  <CoursesOffers
                     data={paginatedCourses(filterCourses(allCourses.open), activePage, PAGE_SIZE)}
                     upcomingCourses={allCourses.upcomingCourses}
                     displayUpcomingCourses={!monthFilter && !academicUnitFilter && !specialtyFilter}
                     isLoading={isLoading}
                     activePage={activePage}
                     pre_inscriptionCourses={allCourses.pre_inscriptions}
                     packs={filterPacks(packs)}
                     packsOnly={academicUnitFilter === PACKS_FILTER}
                     setPage={setPage}
                     totalPages={Math.ceil(filterCourses(allCourses.open)?.length / PAGE_SIZE)}
                     finishedCourses={filterCourses(allCourses.close)}
                  />
               }

            </section>
             <GranDBanner/>
         </MainLayout>
      </>
   );
};
export default CoursesPage;